//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IScope } from 'angular';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import { SubsectionText } from '@components/atoms/text/Text';
import makeAngularReactControllerWithScope from '@react-bridge/angular-react-widget-with-scope';

export interface IntelligentProductTaggingSearchLoaderProps {
  isLoading: boolean;
}

const LOADER_STEPS: readonly { message: string; timingMs: number }[] = [
  { message: 'Scanning image…', timingMs: 0 },
  { message: 'Detecting objects…', timingMs: 4000 },
  { message: 'Identifying products…', timingMs: 10000 },
  { message: 'Finalizing…', timingMs: 17000 },
  {
    message: 'Almost there. This is taking a bit longer than usual...',
    timingMs: 20000,
  },
] as const;

const IntelligentProductTaggingSearchLoader = ({
  isLoading,
}: IntelligentProductTaggingSearchLoaderProps) => {
  const timersRef = useRef<number[]>([]);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const clearTimers = () => {
      timersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      timersRef.current = [];
    };

    if (!isLoading) {
      clearTimers();
      setStepIndex(0);
      return;
    }

    setStepIndex(0);
    clearTimers();

    for (let i = 1; i < LOADER_STEPS.length; i++) {
      const step = LOADER_STEPS[i];
      if (step) {
        const timerId = setTimeout(() => {
          setStepIndex(i);
        }, step.timingMs);
        timersRef.current.push(timerId);
      }
    }

    return () => {
      clearTimers();
    };
  }, [isLoading]);

  if (!isLoading) {
    return null;
  }

  return (
    <SpinnerContainer>
      <StyledInlineSpinner />
      <SubsectionText>{LOADER_STEPS[stepIndex]?.message}</SubsectionText>
    </SpinnerContainer>
  );
};

angular.module('uia').component('intelligentProductTaggingSearchLoader', {
  bindings: {
    isLoading: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (
      scope: {
        $ctrl: IntelligentProductTaggingSearchLoaderProps;
      } & IScope,
    ) => {
      return (
        <IntelligentProductTaggingSearchLoader
          isLoading={scope.$ctrl.isLoading}
        />
      );
    },
  ),
});

const SpinnerContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacings[1]};
  padding: ${theme.spacings[1]} ${theme.spacings[2]};
`,
);

const StyledInlineSpinner = styled(InlineSpinner)(
  ({ theme }) => `
  width: ${theme.spacings[4]};
  height: ${theme.spacings[4]};
`,
);
